import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Users, Download } from 'lucide-react';
import SurnameHeritageChart from './SurnameHeritageChart';
import HeritagePercentageBreakdown from './HeritagePercentageBreakdown';

const SimpleSurnameForm = ({ onAnalysisComplete }) => {
  const [loading, setLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [formData, setFormData] = useState({
    user_full_name: '',
    mother_name: '',
    maternal_grandmother_name: '',
    maternal_grandfather_name: '',
    maternal_great_grandmother_name: '',
    maternal_great_grandfather_name: '',
    father_name: '',
    paternal_grandmother_name: '',
    paternal_grandfather_name: '',
    paternal_great_grandmother_name: '',
    paternal_great_grandfather_name: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least user's name and one family member name is provided
    if (!formData.user_full_name || !formData.user_full_name.trim()) {
      alert('Please enter your full name.');
      return;
    }
    
    const hasFamily = formData.mother_name || formData.father_name || 
                      formData.maternal_grandmother_name || formData.maternal_grandfather_name ||
                      formData.maternal_great_grandmother_name || formData.maternal_great_grandfather_name ||
                      formData.paternal_grandmother_name || formData.paternal_grandfather_name ||
                      formData.paternal_great_grandmother_name || formData.paternal_great_grandfather_name;
    
    if (!hasFamily) {
      alert('Please enter at least one family member name to analyze.');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/surname-analysis/analyze-family-names`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnalysisResults(data);
      if (onAnalysisComplete) {
        onAnalysisComplete(data);
      }
    } catch (error) {
      console.error('Family name analysis error:', error);
      alert('Failed to analyze family names. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!analysisResults) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/surname-analysis/download-surname-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysis_results: analysisResults,
          generated_date: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Create a blob from the response and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `family_heritage_analysis_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  const handleAddToProfile = async () => {
    if (!analysisResults) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        alert('Please log in to add results to your profile.');
        return;
      }

      const response = await fetch(`${backendUrl}/api/profile/add-heritage-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis_results: analysisResults,
          analysis_date: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add results to profile');
      }

      const data = await response.json();
      alert('✅ Heritage analysis successfully added to your profile!');
    } catch (error) {
      console.error('Add to profile error:', error);
      alert(error.message || 'Failed to add results to profile. Please try again.');
    }
  };

  const getFlagEmoji = (countryCode) => {
    if (!countryCode || countryCode === 'XX') return '🏳️';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Name */}
        <div>
          <Label htmlFor="user_full_name" className="text-base font-semibold">Your Full Name *</Label>
          <Input
            id="user_full_name"
            value={formData.user_full_name}
            onChange={(e) => handleInputChange('user_full_name', e.target.value)}
            placeholder="Enter your full name"
            required
          />
        </div>

        {/* Maternal Line */}
        <Card className="bg-purple-50/50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-xl mb-4 flex items-center">
              <Users className="mr-2 h-6 w-6 text-purple-600" />
              Maternal Line (Mother's Side)
            </h3>
            
            {/* Mother */}
            <div className="mb-4">
              <Label htmlFor="mother_name" className="text-base">Mother's Full Name</Label>
              <Input
                id="mother_name"
                value={formData.mother_name}
                onChange={(e) => handleInputChange('mother_name', e.target.value)}
                placeholder="Enter your mother's full name"
              />
            </div>

            {/* Maternal Grandparents */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="maternal_grandmother_name" className="text-base">Maternal Grandmother's Full Name</Label>
                <Input
                  id="maternal_grandmother_name"
                  value={formData.maternal_grandmother_name}
                  onChange={(e) => handleInputChange('maternal_grandmother_name', e.target.value)}
                  placeholder="Maternal grandmother's full name"
                />
              </div>
              <div>
                <Label htmlFor="maternal_grandfather_name" className="text-base">Maternal Grandfather's Full Name</Label>
                <Input
                  id="maternal_grandfather_name"
                  value={formData.maternal_grandfather_name}
                  onChange={(e) => handleInputChange('maternal_grandfather_name', e.target.value)}
                  placeholder="Maternal grandfather's full name"
                />
              </div>
            </div>

            {/* Maternal Great-Grandparents */}
            <div className="border-t border-purple-200 pt-4">
              <h4 className="font-medium text-sm text-purple-800 mb-3">Maternal Great-Grandparents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maternal_great_grandmother_name" className="text-sm">Great-Grandmother's Full Name</Label>
                  <Input
                    id="maternal_great_grandmother_name"
                    value={formData.maternal_great_grandmother_name}
                    onChange={(e) => handleInputChange('maternal_great_grandmother_name', e.target.value)}
                    placeholder="Maternal great-grandmother's name"
                  />
                </div>
                <div>
                  <Label htmlFor="maternal_great_grandfather_name" className="text-sm">Great-Grandfather's Full Name</Label>
                  <Input
                    id="maternal_great_grandfather_name"
                    value={formData.maternal_great_grandfather_name}
                    onChange={(e) => handleInputChange('maternal_great_grandfather_name', e.target.value)}
                    placeholder="Maternal great-grandfather's name"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paternal Line */}
        <Card className="bg-blue-50/50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-xl mb-4 flex items-center">
              <Users className="mr-2 h-6 w-6 text-blue-600" />
              Paternal Line (Father's Side)
            </h3>
            
            {/* Father */}
            <div className="mb-4">
              <Label htmlFor="father_name" className="text-base">Father's Full Name</Label>
              <Input
                id="father_name"
                value={formData.father_name}
                onChange={(e) => handleInputChange('father_name', e.target.value)}
                placeholder="Enter your father's full name"
              />
            </div>

            {/* Paternal Grandparents */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="paternal_grandmother_name" className="text-base">Paternal Grandmother's Full Name</Label>
                <Input
                  id="paternal_grandmother_name"
                  value={formData.paternal_grandmother_name}
                  onChange={(e) => handleInputChange('paternal_grandmother_name', e.target.value)}
                  placeholder="Paternal grandmother's full name"
                />
              </div>
              <div>
                <Label htmlFor="paternal_grandfather_name" className="text-base">Paternal Grandfather's Full Name</Label>
                <Input
                  id="paternal_grandfather_name"
                  value={formData.paternal_grandfather_name}
                  onChange={(e) => handleInputChange('paternal_grandfather_name', e.target.value)}
                  placeholder="Paternal grandfather's full name"
                />
              </div>
            </div>

            {/* Paternal Great-Grandparents */}
            <div className="border-t border-blue-200 pt-4">
              <h4 className="font-medium text-sm text-blue-800 mb-3">Paternal Great-Grandparents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paternal_great_grandmother_name" className="text-sm">Great-Grandmother's Full Name</Label>
                  <Input
                    id="paternal_great_grandmother_name"
                    value={formData.paternal_great_grandmother_name}
                    onChange={(e) => handleInputChange('paternal_great_grandmother_name', e.target.value)}
                    placeholder="Paternal great-grandmother's name"
                  />
                </div>
                <div>
                  <Label htmlFor="paternal_great_grandfather_name" className="text-sm">Great-Grandfather's Full Name</Label>
                  <Input
                    id="paternal_great_grandfather_name"
                    value={formData.paternal_great_grandfather_name}
                    onChange={(e) => handleInputChange('paternal_great_grandfather_name', e.target.value)}
                    placeholder="Paternal great-grandfather's name"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button 
          type="submit" 
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing Family Heritage...
            </>
          ) : (
            'Generate Heritage Analysis'
          )}
        </Button>
      </form>

      {/* Results */}
      {analysisResults && (
        <div className="space-y-6 mt-8">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <h3 className="text-2xl font-bold">Analysis Results</h3>
            <div className="flex gap-3">
              <Button onClick={handleAddToProfile} variant="default" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Add Results to My Profile
              </Button>
              <Button onClick={handleDownload} variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download Report
              </Button>
            </div>
          </div>

          {/* Percentage Breakdowns */}
          {analysisResults.surname_results && analysisResults.surname_results.length > 0 && (
            <HeritagePercentageBreakdown surnameResults={analysisResults.surname_results} />
          )}

          {/* Heritage Distribution Chart */}
          {analysisResults.surname_results && analysisResults.surname_results.length > 0 && (
            <SurnameHeritageChart 
              surnameResults={analysisResults.surname_results}
              includeGermanicTribes={true}
            />
          )}

          {/* Analysis Notes */}
          {analysisResults.analysis_notes && analysisResults.analysis_notes.length > 0 && (
            <Card className="bg-blue-50/50">
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-2">Analysis Summary:</h4>
                <ul className="list-disc list-inside space-y-1">
                  {analysisResults.analysis_notes.map((note, index) => (
                    <li key={index} className="text-sm text-gray-700">{note}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Individual Surname Results */}
          <div className="space-y-4">
            {analysisResults.surname_results && analysisResults.surname_results.map((result, index) => (
              <Card key={index} className="border-l-4 border-l-blue-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold flex items-center gap-2">
                      {getFlagEmoji(result.country_code)}
                      {result.surname}
                    </h3>
                    <span className="text-sm text-gray-500">{result.country_origin}</span>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-gray-50 p-3 rounded">
                      <h4 className="font-medium text-gray-800 mb-2">Etymology & Meaning:</h4>
                      <p className="text-gray-700">{result.etymology}</p>
                    </div>

                    {result.spelling_variations && result.spelling_variations.length > 0 && (
                      <div className="bg-yellow-50 p-3 rounded">
                        <h4 className="font-medium text-yellow-800 mb-2">Spelling Variations:</h4>
                        <p className="text-yellow-700">{result.spelling_variations.join(', ')}</p>
                      </div>
                    )}

                    {result.geographic_distribution && (
                      <div className="bg-green-50 p-3 rounded">
                        <h4 className="font-medium text-green-800 mb-2">Geographic Distribution:</h4>
                        <p className="text-green-700">{result.geographic_distribution}</p>
                      </div>
                    )}

                    {result.cultural_context && (
                      <div className="bg-blue-50 p-3 rounded">
                        <h4 className="font-medium text-blue-800 mb-2">Cultural Context:</h4>
                        <p className="text-blue-700">{result.cultural_context}</p>
                      </div>
                    )}

                    {result.historical_significance && (
                      <div className="bg-red-50 p-3 rounded">
                        <h4 className="font-medium text-red-800 mb-2">Historical Significance:</h4>
                        <p className="text-red-700">{result.historical_significance}</p>
                      </div>
                    )}
                    
                    {result.germanic_tribe && result.germanic_tribe.tribe_name && (
                      <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                        <div className="flex items-center mb-2">
                          <svg className="w-5 h-5 mr-2 text-purple-700" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                          </svg>
                          <h4 className="font-bold text-purple-900">Germanic Tribe Origin: {result.germanic_tribe.tribe_name}</h4>
                        </div>
                        
                        {result.germanic_tribe.tribe_history && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-purple-800 mb-1">Tribe History:</p>
                            <p className="text-sm text-purple-700 leading-relaxed">{result.germanic_tribe.tribe_history}</p>
                          </div>
                        )}
                        
                        {result.germanic_tribe.surname_connection && (
                          <div>
                            <p className="text-sm font-semibold text-purple-800 mb-1">Connection to {result.surname}:</p>
                            <p className="text-sm text-purple-700 leading-relaxed">{result.germanic_tribe.surname_connection}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleSurnameForm;
